import { MutationCache, QueryClient } from "@tanstack/react-query";
import { guardianDenialFromError, isApiError } from "@bw/shared/api";
import { publishGuardianDenial } from "../guardian/denials";

/**
 * Data-layer seed. Slice 0 has no endpoints yet, so there are no hooks — but the
 * QueryClient factory and the query-key factory establish the pattern every
 * later journey slice plugs into (one client per app, keys namespaced per
 * domain so cache invalidation stays surgical).
 */
declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: {
      /** "inline": this mutation shows guardian denials in its own error area. */
      guardianPrompt?: "inline";
    };
  }
}

export function createQueryClient(): QueryClient {
  let queryClient: QueryClient;
  const mutationCache = new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (isApiError(error) && error.code === "ACTIVE_BAN") {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.moderation.myStatus(),
        });
      }
      // Guardian activity controls (#2691): one shared answer for every
      // controlled action, on every screen (app shell GuardianPrompt). A
      // mutation with its own error area opts out with
      // `meta: { guardianPrompt: "inline" }` and renders GuardianDenialNotice
      // there instead, so the child never sees two contradictory messages.
      const denial = guardianDenialFromError(error);
      if (denial) {
        if (mutation.meta?.guardianPrompt !== "inline") publishGuardianDenial(denial);
        // Someone else's approval doesn't change the caller's own controls.
        if (denial.code !== "GUARDIAN_MEMBER_APPROVAL_REQUIRED") {
          void queryClient.invalidateQueries({
            queryKey: queryKeys.guardian.myControls(),
          });
        }
      }
    },
  });
  queryClient = new QueryClient({
    mutationCache,
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60, // 60s — matches the platform app
        retry: 1,
      },
    },
  });
  return queryClient;
}

/**
 * Query-key factory seed. Each journey owns a namespace; keys are tuples so
 * React Query can do prefix-based invalidation (e.g. invalidate all of `play`).
 * Empty arms today — populated as each slice adds its real endpoints.
 */
export const queryKeys = {
  moderation: {
    all: ["moderation"] as const,
    myStatus: () => ["moderation", "me", "status"] as const,
  },
  home: {
    all: ["home"] as const,
  },
  /**
   * Play's current next-session read (GET /me/bookings) uses a distinct cache
   * slot. The backend scopes it to the token user. Personal rating is owned
   * exclusively by Performance, not duplicated in this namespace.
   */
  play: {
    all: ["play"] as const,
    myUpcoming: () => ["play", "my-upcoming"] as const,
  },
  /**
   * Compete journey (rating, stats, leaderboard, head-to-head, achievements).
   * Prefix-tuple keys so React Query can invalidate the whole journey via the
   * "compete" arm, while each surface gets a DISTINCT cache slot: a different
   * facility / limit / offset (or the global board, facility=null) never shares
   * a slot with another. All read-only — slice 5a.
   */
  compete: {
    all: ["compete"] as const,
    myRating: () => ["compete", "my-rating"] as const,
    rating: (userId: string) => ["compete", "rating", userId] as const,
    leaderboard: (
      facilityId: string | undefined,
      limit: number,
      offset: number,
    ) => ["compete", "leaderboard", facilityId ?? null, limit, offset] as const,
    myStats: () => ["compete", "my-stats"] as const,
    playerStats: (userId: string, facilityId?: string) =>
      ["compete", "player-stats", userId, facilityId ?? null] as const,
    headToHead: (a: string, b: string) =>
      ["compete", "head-to-head", a, b] as const,
    achievements: (userId: string) =>
      ["compete", "achievements", userId] as const,
    // A player's profile stats payload (GET /analytics/players/{userId}/profile):
    // the singles/doubles split + win rate + games + their last-10 recent_matches.
    // Keyed by the PROFILED userId so each player gets a distinct slot — the same
    // slot backs public Player profiles and personal Performance. Successful
    // result reporting invalidates the shared performance query family.
    profile: (userId: string) => ["compete", "profile", userId] as const,
    /** Where a player plays and their circles (GET /users/{id}/profile-summary). */
    profileSummary: (userId: string) =>
      ["compete", "profile-summary", userId] as const,
    // A single ranked match (GET /ranking/matches/{match_id}), backing the
    // report-result surface (Player Slice 3). Keyed by match id so each match
    // gets a distinct cache slot; successful reporting refreshes result and
    // performance reads to reflect the new server status.
    match: (matchId: string) => ["compete", "match", matchId] as const,
    // The caller's OWN match history (GET /ranking/matches), SERVER-SCOPED to the
    // auth user — no uid is ever in the key. Optional facility / limit filters keep
    // distinct filters in distinct cache slots (a global, all-facility history at
    // facility=null never shares a slot with a facility-scoped one). Read-only
    // (Player Slice 4).
    history: (params?: { facilityId?: string; limit?: number }) =>
      [
        "compete",
        "history",
        params?.facilityId ?? null,
        params?.limit ?? null,
      ] as const,
  },
  /**
   * Social journey (communities, feed, friends). The Communities hub backs BOTH
   * its Discover and My Communities tabs from ONE `communities()` list fetch (the
   * is_member split is a pure client-side view filter), so a single cache slot is
   * correct. A community's detail surface is keyed by its id. Join / leave
   * invalidate the `social` arm (prefix) AND the specific `community(id)` slot so
   * both the hub list and the open detail page refetch the server's truth — never
   * an optimistic local member_count bump.
   */
  social: {
    all: ["social"] as const,
    communities: () => ["social", "communities"] as const,
    admissionsMine: () => ["social", "admissions", "mine"] as const,
    community: (communityId: string) =>
      ["social", "community", communityId] as const,
    communityChannels: (communityId: string) =>
      ["social", "community", communityId, "channels"] as const,
    communityGameNights: (communityId: string, includePast: boolean) =>
      ["social", "community", communityId, "game-nights", includePast] as const,
    communityPolls: (communityId: string) =>
      ["social", "community", communityId, "polls"] as const,
    communityNotificationPreferences: (communityId: string) =>
      ["social", "community", communityId, "notification-preferences"] as const,
    // Post detail and comment threads remain Community-owned. Feed lists use the
    // shared canonical Feed key family, never this Social cache arm.
    post: (postId: string) => ["social", "post", postId] as const,
    postComments: (postId: string) =>
      ["social", "post-comments", postId] as const,
    // Slice 4a (friends). The accepted-friends list (GET /social/friends) and the
    // pending friend-request inbox (GET /social/friends/requests) each get a
    // DISTINCT cache slot — no params, the backend scopes both to the token user.
    // A lifecycle mutation invalidates surgically: accept → both keys (a new
    // friend appears + the request leaves); decline / cancel → friendRequests
    // only; unfriend → friends only. Never an optimistic local edit.
    friends: () => ["social", "friends"] as const,
    friendRequests: () => ["social", "friend-requests"] as const,
    // Token-scoped directional follow state for one viewed player. Distinct
    // from friendship: following controls Home audience sources but does not
    // imply a reciprocal connection.
    followStatus: (userId: string) =>
      ["social", "follow-status", userId] as const,
    // Token-scoped block recovery. The list contains only the viewer's outgoing
    // blocks; the per-player status is keyed by the opaque target id but that id
    // is never rendered as copy. Block/unblock invalidate both server truths.
    blockedUsers: () => ["social", "blocked-users"] as const,
    blockStatus: (userId: string) =>
      ["social", "block-status", userId] as const,
    // Slice 4b (find players / add a friend). A user search (GET
    // /social/users/search?q=) keyed by the trimmed query so each query gets a
    // DISTINCT cache slot. Enabled only when the debounced trimmed query is ≥ 2
    // chars (the server requires q min_length=2). The Add mutation invalidates
    // `friendRequests` (not this key) — the row's "Requested" state is derived
    // from the refetched outgoing inbox, never an optimistic local flip.
    userSearch: (query: string) => ["social", "user-search", query] as const,
    // EPIC P.1 (general player search). The draw-INDEPENDENT rich-card search
    // (GET /social/players/search?q=&scope=) keyed by the trimmed query + scope
    // so each distinct (query, scope) search gets its OWN cache slot and never
    // collides with `userSearch`. Enabled only when the debounced trimmed query
    // is ≥ 2 chars (the server requires q min_length=2). Returns the server's
    // PlayerCard[] verbatim — no client sort, no derived relationship/rating.
    // (The `facility` scope was removed for privacy — #1657.)
    playerSearch: (query: string, scope: string) =>
      ["social", "player-search", scope, query] as const,
    // Slice F1 (DM inbox). The caller's conversation list (GET
    // /social/conversations) — ONE cache slot, no params (the backend scopes the
    // list to the token user). The inbox hook polls this key on an interval to
    // keep unread_count / last_message fresh; a read mutation (mark-read, sending
    // a message — later slices) invalidates it for the server's refetched truth.
    conversations: () => ["social", "conversations"] as const,
    // Slice F2 (DM thread). A single conversation's message page (GET
    // /social/conversations/{id}/messages) keyed by the conversation id so each
    // open thread gets a DISTINCT cache slot. Full history and the Dock's latest-
    // only projection use separate slots so a bounded Dock poll can never rewrite
    // the thread's loaded keyset pages.
    messages: (id: string) => ["direct-messages", id, "history"] as const,
    latestMessages: (id: string) => ["direct-messages", id, "latest"] as const,
    messageReadAuthority: (id: string) =>
      ["direct-messages", id, "read-authority"] as const,
    conversationMessages: (id: string) => ["direct-messages", id] as const,
    allMessages: ["direct-messages"] as const,
  },
  me: {
    all: ["me"] as const,
    /**
     * Account surfaces under /me (Account → Transactions + Privacy & data).
     * `transactions` backs the read-only payment history (GET /transactions/history,
     * SERVER-SCOPED to the auth user — no uid is ever in the key). `deletionStatus`
     * backs the CCPA right-to-delete tombstone read (GET /me/deletion-status) — a
     * deletion mutation invalidates this arm so the page re-reads the server's
     * truth (never an optimistic flip). Both no-params: the backend scopes
     * to the token user.
     *
     * `paymentMethods` backs the player's CROSS-ORG saved cards (GET
     * /users/me/payment-methods, #1392) — SERVER-SCOPED to the auth user, no uid
     * in the key. A remove mutation (DELETE /users/me/payment-methods/{composite})
     * invalidates this arm so the list re-reads the server's truth — never an
     * optimistic local removal.
     *
     * `leagueRegistrations` backs the player's CROSS-FACILITY "My leagues" history
     * (GET /users/me/league-registrations) — SERVER-SCOPED to the auth user, no uid
     * in the key, read-only. The league analogue of the tournament
     * `myRegistrations` list; its OWN cache slot so the two never collide.
     */
    transactions: () => ["me", "transactions"] as const,
    transaction: (transactionId: string) =>
      ["me", "transactions", transactionId] as const,
    phone: () => ["me", "phone"] as const,
    smsConsent: () => ["me", "sms-consent"] as const,
    deletionStatus: () => ["me", "deletion-status"] as const,
    paymentMethods: () => ["me", "payment-methods"] as const,
    leagueRegistrations: () => ["me", "league-registrations"] as const,
  },
  /**
   * Memberships journey (facility plan catalog → subscribe → manage). Prefix
   * tuples so a lifecycle mutation can invalidate the whole journey via the
   * `memberships` arm while each surface keeps a DISTINCT cache slot:
   *
   * `facilityPlans` — a facility's ACTIVE player-facing plans (GET
   * /facilities/{id}/plans), keyed by facility id so each catalog is isolated
   * (read-only; a subscribe never mutates the catalog).
   *
   * `mine` / `visitBalances` — the caller's subscriptions (GET /memberships/) and
   * visit balances (GET /memberships/visit-balances), each ONE cache slot, no
   * params (the backend scopes both to the token user, so no uid is ever in the
   * key). `detail(id)` / `visitBalance(id)` are the single-subscription reads,
   * keyed by subscription id so each open membership is card-isolated. A subscribe
   * invalidates `mine` + `visitBalances`; a cancel / reactivate invalidates `mine`
   * + the specific `detail(id)` — so every surface re-reads the server's truth,
   * never an optimistic local status flip.
   */
  memberships: {
    all: ["memberships"] as const,
    facilityPlans: (facilityId: string) =>
      ["memberships", "facility-plans", facilityId] as const,
    mine: () => ["memberships", "mine"] as const,
    detail: (subscriptionId: string) =>
      ["memberships", "detail", subscriptionId] as const,
    visitBalances: () => ["memberships", "visit-balances"] as const,
    visitBalance: (subscriptionId: string) =>
      ["memberships", "visit-balance", subscriptionId] as const,
    // Included-sessions usage this period for one subscription+service (Slice
    // 6.2, GET /memberships/{id}/entitlements/{service}/usage). Keyed by BOTH
    // so a subscription with entitlements on more than one service never
    // collides in the cache.
    entitlementUsage: (subscriptionId: string, service: string) =>
      ["memberships", "entitlement-usage", subscriptionId, service] as const,
  },
  /**
   * Parent-managed under-13 consent (COPPA, #1309 Part A). Two SERVER-SCOPED
   * (no-uid-in-key) reads, each in its own cache slot:
   *
   * `linkStatus` backs the CHILD's own guardian-link status (GET
   * /users/me/guardian-link → pending|active|revoked|null) — read in onboarding's
   * AgeOutcome. A requestGuardianLink mutation seeds/invalidates it so the child's
   * panel re-reads the server's truth (never an optimistic local flip).
   *
   * `childApprovals` backs the PARENT's pending child requests (GET
   * /users/me/child-approvals) — read on the Family approvals page. An approve /
   * revoke mutation invalidates it so the list re-reads the server's truth.
   *
   * `activeChildren` backs the PARENT's APPROVED-children roster (GET
   * /users/me/child-approvals?status=active) — the COPPA right-to-withdraw
   * surface (#1630), read on the same page. approve (a child becomes active) and
   * revoke (a child leaves the roster) both invalidate it.
   */
  guardian: {
    all: ["guardian"] as const,
    linkStatus: () => ["guardian", "link-status"] as const,
    childApprovals: () => ["guardian", "child-approvals"] as const,
    activeChildren: () => ["guardian", "active-children"] as const,
    child: (linkId: string) => ["guardian", "child", linkId] as const,
    // Guardian activity controls (#2691): the caller's own effective controls
    // (look-only, Decline/Ask/Approve) and the guardian's request inbox.
    myControls: () => ["guardian", "my-controls"] as const,
    familyRequests: (status: "pending" | "approved" | "all") =>
      ["guardian", "family-requests", status] as const,
  },
  /**
   * Booking journey (court sheet + cart). Keys are prefix-tuples so React Query
   * can invalidate surgically — e.g. all of a facility's booking data, or just
   * one day's availability. Availability is keyed by (facilityId, date) since a
   * different day is a different fetch.
   */
  booking: {
    all: ["booking"] as const,
    facilities: (params?: {
      query?: string;
      city?: string;
      latitude?: number;
      longitude?: number;
      radius_km?: number;
      open_now?: boolean;
      service?: string;
      limit?: number;
      offset?: number;
    }) => ["booking", "facilities", params ?? {}] as const,
    facility: (facilityId: string) =>
      ["booking", "facility", facilityId] as const,
    publicCampaigns: (facilityId: string) =>
      ["booking", "public-campaigns", facilityId] as const,
    courts: (facilityId: string) => ["booking", "courts", facilityId] as const,
    operatingHours: (facilityId: string) =>
      ["booking", "operating-hours", facilityId] as const,
    availability: (facilityId: string, date: string) =>
      ["booking", "availability", facilityId, date] as const,
    pricing: (facilityId: string) =>
      ["booking", "pricing", facilityId] as const,
    // Checkout (slice 3b + slice ② allocation). The cart quote is keyed by
    // Include payer and campaign: eligibility is personal, and changing an offer
    // must fetch its own price instead of reusing another selection's total.
    quote: (
      facilityId: string,
      slotKeys: string[],
      timezone: string,
      playerCount = 1,
      campaignId: string | null = null,
      skipPromotions = false,
      userId: string | null = null,
    ) =>
      [
        "booking",
        "quote",
        facilityId,
        slotKeys,
        timezone,
        playerCount,
        campaignId,
        skipPromotions,
        userId,
      ] as const,
    // NOTE: the facility-wallet payment keys (payment-methods / frontend-config /
    // setup-intent) moved to @bw/shared `paymentQueryKeys` when the Stripe spine
    // was promoted into the shared package — import them from there. The tuples are
    // still `["booking", …]` so the shared cache slots are unchanged.
    // My bookings (slice 3c). `myBookingsPrefix` invalidates every facility list
    // after an invitation accept makes the caller a participant; the facility id
    // remains part of each actual cache slot.
    myBookingsPrefix: () => ["booking", "my-bookings"] as const,
    myBookings: (facilityId: string) =>
      ["booking", "my-bookings", facilityId] as const,
    // The caller's bookings across ALL facilities (GET /me/bookings), backing the
    // cross-facility calendar. Distinct from `myBookings` (facility-scoped) so the
    // two never share a cache slot. No params — the backend scopes to the token user.
    allBookings: () => ["booking", "all-bookings"] as const,
    // The booking DETAIL hub (slice δ). `detail` is keyed by the booking id so
    // each open booking is card-isolated (GET /facilities/bookings/{id}).
    // `participants` is the per-booking roster (GET /facilities/{facilityId}/
    // bookings/{bookingId}/participants), keyed by (facilityId, bookingId) so a
    // roster fetch never collides with another booking's. An add/remove mutation
    // invalidates THIS key so the roster re-reads the server's truth — never an
    // optimistic fabricated row. `pass` is the short-lived QR check-in pass for a
    // booking (GET /checkin/bookings/{id}/pass), keyed by the booking id; the
    // refresh path re-fetches it (the displayed token never goes stale).
    detail: (bookingId: string) => ["booking", "detail", bookingId] as const,
    participants: (facilityId: string, bookingId: string) =>
      ["booking", "participants", facilityId, bookingId] as const,
    pass: (bookingId: string) => ["booking", "pass", bookingId] as const,
    // Whether each of my bookings here can show a check-in QR now (#2691).
    passStates: (facilityId: string) => ["booking", "pass-states", facilityId] as const,
    // Court invitations (Slice α). `myInvitations` backs the caller's INCOMING
    // pending court-invite inbox (GET /users/me/booking-invitations) — ONE cache
    // slot, no params (the backend scopes the list to the token user, so no uid is
    // ever in the key). `invitations` backs a booking's OUTGOING pending-invite
    // list (GET /facilities/{facilityId}/bookings/{bookingId}/invitations), keyed
    // by (facilityId, bookingId) so a sent-invites fetch never collides with
    // another booking's. A respond (accept/decline) invalidates `myInvitations` +
    // `participants` + `detail`; a create invalidates `invitations` — so each
    // surface re-reads the server's truth, never an optimistic local edit.
    myInvitations: () => ["booking", "my-invitations"] as const,
    invitations: (facilityId: string, bookingId: string) =>
      ["booking", "invitations", facilityId, bookingId] as const,
  },
  /**
   * Drop-in journey (discovery + check-in + waiver gate). Keys are
   * prefix-tuples for surgical invalidation — e.g. a facility's drop-in session
   * after a successful check-in, or a facility's waiver status after acceptance.
   */
  dropin: {
    all: ["dropin"] as const,
    openPlay: (latitude?: number, longitude?: number) =>
      ["dropin", "open-play", latitude ?? null, longitude ?? null] as const,
    queueStats: (facilityId: string) =>
      ["dropin", "queue-stats", facilityId] as const,
    paymentOptions: (facilityId: string) =>
      ["dropin", "payment-options", facilityId] as const,
    sessionAll: ["dropin", "session"] as const,
    session: (facilityId: string) => ["dropin", "session", facilityId] as const,
    cancellation: (facilityId: string, sessionId: string) =>
      ["dropin", "session", facilityId, sessionId, "cancellation"] as const,
    currentSessions: () => ["dropin", "current-sessions"] as const,
    waiverStatus: (facilityId: string) =>
      ["dropin", "waiver-status", facilityId] as const,
    waiver: (facilityId: string) => ["dropin", "waiver", facilityId] as const,
    // Live court-queue surfaces (the post-check-in queue flow). Keyed by the
    // entry/assignment id so a poll for one entry never collides with another.
    myQueueEntry: (facilityId: string) =>
      ["dropin", "my-entry", facilityId] as const,
    queueEntry: (entryId: string) =>
      ["dropin", "queue-entry", entryId] as const,
    queueAssignment: (entryId: string) =>
      ["dropin", "queue-entry", entryId, "assignment"] as const,
    courts: (facilityId: string) => ["dropin", "courts", facilityId] as const,
    // The QR check-in pass for the caller's CURRENT drop-in session at a
    // facility (active OR pending_validation), keyed by facility id. The old
    // per-source builder (dropin/queue/assignment) was retired with the
    // frictionless rework — only the drop-in pass remains (the staff
    // QR-validation gate), so the key is just the facility id.
    pass: (facilityId: string) => ["dropin", "pass", facilityId] as const,
    // S2.3 — the facility's single active community ("club"), surfaced as a
    // SECONDARY section on the drop-in/facility detail page (GET
    // /communities/facility/{id} → CommunityResponse | null). Keyed by facility
    // id so each facility's community read is card-isolated. Read-only here: the
    // card routes into /community/communities/:id which owns join/leave + feed.
    facilityCommunity: (facilityId: string) =>
      ["dropin", "facility-community", facilityId] as const,
    // Slice 3b — the players currently checked in at a facility (party-invite
    // picker). Keyed by facility id; gated to callers checked in at that facility
    // (403 if not). The backend returns them in last_active_at DESC order; the
    // FE renders in that order, no re-sort.
    checkedInPlayers: (facilityId: string) =>
      ["dropin", "checked-in-players", facilityId] as const,
    // Phase 2 P2.S2 — self-serve court board. Keyed by facility id so each
    // facility's board state is card-isolated. Join/leave mutations invalidate
    // this key so the board re-reads the server's truth — never an optimistic
    // local state flip (the backend is authoritative on court state).
    courtBoard: (facilityId: string) =>
      ["dropin", "court-board", facilityId] as const,
    myParty: (facilityId: string) =>
      ["dropin", "my-party", facilityId] as const,
    partyMemberships: (facilityId: string) =>
      ["dropin", "party-memberships", facilityId] as const,
    // Phase 2 P2.S2 — the facility row (GET /facilities/{id}), read ONLY for its
    // `queue_assignment_mode` so the session hub routes a self-serve player to
    // the court board (vs. the queue CTA for auto/manual). A dropin-namespaced
    // slot (NOT booking.facility) keeps this journey's read independent of the
    // booking module's facility cache. Read-only.
    facility: (facilityId: string) =>
      ["dropin", "facility", facilityId] as const,
  },
  /**
   * Party invites (drop-in group play). `invitesMine` backs every caller-facing
   * incoming-invite surface (GET /queue/party-invites/mine) — ONE cache slot, no
   * params (the backend scopes the list to the token user; facility hubs filter
   * the same response by exact facility id). `sentInvites` backs a
   * party's OUTGOING pending-invite list (GET /queue/party/{id}/invites), keyed by
   * the party id (the "Invited (pending) · Cancel" list on party detail). `detail`
   * is keyed by the party id so each party's roster is card-isolated. A respond /
   * invite / cancel mutation invalidates the relevant arm(s) so the inbox + sent
   * list + roster reflect the server's refetched truth — never an optimistic local
   * edit.
   */
  party: {
    all: ["party"] as const,
    invitesMine: () => ["party", "invites", "mine"] as const,
    sentInvites: (partyId: string) =>
      ["party", "invites", "sent", partyId] as const,
    detail: (partyId: string) => ["party", "detail", partyId] as const,
    // Task 6 — the leader's "recent + search" invite-candidates picker (GET
    // /queue/party/{id}/invite-candidates). Keyed by (partyId, q) so the default
    // "recently active" list (q="") and each distinct search term get their own
    // cache slot. A successful invite invalidates the WHOLE (partyId) prefix
    // (see useInviteToParty) so every q-variant re-reads server truth — the
    // invitee is excluded server-side on the next fetch.
    inviteCandidates: (partyId: string, q: string) =>
      ["party", "invite-candidates", partyId, q] as const,
  },
  /**
   * Classes & clinics journey (unified program module). Facility-first discovery
   * reuses the booking `facilities` search cache; everything else is keyed by the
   * program / track / enrollment id so invalidation stays surgical. `sessions` is
   * keyed by (programId, window) since a different window is a different fetch.
   * `myEnrollments` has no params — the backend scopes to the token user.
   */
  classes: {
    all: ["classes"] as const,
    facilityPrograms: (facilityId: string, programType?: string) =>
      [
        "classes",
        "facility-programs",
        facilityId,
        programType ?? null,
      ] as const,
    program: (programId: string) => ["classes", "program", programId] as const,
    tracks: (programId: string) => ["classes", "tracks", programId] as const,
    sessions: (
      programId: string,
      windowStart: string,
      windowEnd: string,
      trackId?: string,
    ) =>
      [
        "classes",
        "sessions",
        programId,
        windowStart,
        windowEnd,
        trackId ?? null,
      ] as const,
    enrollmentQuote: (
      trackId: string,
      mode: string,
      occurrenceStart: string | null,
    ) =>
      ["classes", "enrollment-quote", trackId, mode, occurrenceStart] as const,
    myEnrollments: () => ["classes", "my-enrollments"] as const,
    // Slice 6.1 (offering-keyed class pricing): a track's price is NOT on
    // ProgramTrackResponse -- it's a service="classes", offering_id=track_id
    // pricing rule. Keyed by facility (the rules list is fetched once per
    // facility and matched per-track client-side, mirroring the booking
    // module's `booking.pricing`).
    pricingRules: (facilityId: string) =>
      ["classes", "pricing-rules", facilityId] as const,
  },
  /**
   * Coaches discovery journey (player-callable coach reads + lesson requests).
   * The list is keyed by its facility/status filters so distinct filters live in
   * distinct cache slots; the four detail surfaces (profile, stats, services,
   * affiliations) are each keyed by the coach_user_id so they stay card-isolated
   * — a failing stats fetch never blanks the profile. `facility` resolves an
   * affiliation's facility_id → name (reused for the request-a-lesson picker).
   * All read-only; the lesson-request CREATE is a mutation (no cache key).
   */
  coaches: {
    all: ["coaches"] as const,
    list: (
      facilityId: string | undefined,
      status: string | undefined,
      limit: number | undefined,
      offset: number | undefined,
    ) =>
      [
        "coaches",
        "list",
        facilityId ?? null,
        status ?? null,
        limit ?? null,
        offset ?? null,
      ] as const,
    coach: (coachUserId: string) => ["coaches", "coach", coachUserId] as const,
    stats: (coachUserId: string) => ["coaches", "stats", coachUserId] as const,
    services: (coachUserId: string) =>
      ["coaches", "services", coachUserId] as const,
    affiliations: (coachUserId: string) =>
      ["coaches", "affiliations", coachUserId] as const,
    facility: (facilityId: string) =>
      ["coaches", "facility", facilityId] as const,
    // The caller's OWN lesson requests (GET /lesson-requests), SERVER-SCOPED to
    // the token user — no uid is ever in the key. An optional status filter keeps
    // distinct filters in distinct cache slots. A successful pay
    // (POST /lesson-requests/{id}/pay) invalidates this arm so the paid row
    // reflects the server's refetched truth — never an optimistic local flip.
    myLessons: (status?: string) =>
      ["coaches", "my-lessons", status ?? null] as const,
  },
  /**
   * Leagues discovery journey (player S3b — browse a facility's leagues, then
   * drill seasons → divisions to self-register). All anonymous-readable reads,
   * each keyed by its parent id so the three levels stay in DISTINCT cache slots:
   * a facility's leagues, a league's seasons, a season's divisions. `facilityLeagues`
   * takes an optional status filter (distinct filters → distinct slots). A successful
   * register invalidates `queryKeys.me.leagueRegistrations()` (the cross-facility "My
   * leagues" arm) — NOT these browse keys — so a newly-joined division surfaces in S1.
   * The division browse may also be re-read after a register to reflect the bumped
   * registration_count (server truth), never an optimistic local count.
   */
  leagues: {
    all: ["leagues"] as const,
    facilityLeagues: (facilityId: string, status?: string) =>
      ["leagues", "facility-leagues", facilityId, status ?? null] as const,
    seasons: (leagueId: string) => ["leagues", "seasons", leagueId] as const,
    divisions: (seasonId: string) =>
      ["leagues", "divisions", seasonId] as const,
  },
  /**
   * Tournaments discovery journey (player-callable, anonymous-readable reads).
   * `browse` is keyed by (status, offset) so each filter + page lives in a
   * DISTINCT cache slot — switching the status filter or paging never reads a
   * stale list. `detail` is keyed by the tournament id so each detail page is
   * card-isolated. Both read-only; no mutation invalidates these.
   */
  tournaments: {
    all: ["tournaments"] as const,
    browse: (status: string | undefined, offset: number) =>
      ["tournaments", "browse", status ?? null, offset] as const,
    detail: (tournamentId: string) =>
      ["tournaments", "detail", tournamentId] as const,
    archive: (tournamentId: string) =>
      ["tournaments", "archive", tournamentId] as const,
    archiveRelease: (tournamentId: string) =>
      ["tournaments", "archive", tournamentId, "release"] as const,
    // The caller's own archive certificates. The server scopes this read to the
    // token identity, so no user id belongs in a client cache key.
    archiveCertificatesMine: (tournamentId: string) =>
      ["tournaments", "archive", tournamentId, "certificates", "mine"] as const,
    // The tournament's events list (GET /tournaments/{id}/events). A SEPARATE
    // cache slot + query from `detail` so the Events section's loading/error is
    // isolated — a failing events fetch never blanks the main detail.
    events: (tournamentId: string) =>
      ["tournaments", "events", tournamentId] as const,
    // The tournament's draws list (GET /tournaments/{id}/draws). Its OWN cache
    // slot + query so the Draws section is isolated like Events — a failing
    // draws fetch surfaces the section's alert without blanking the detail.
    draws: (tournamentId: string) =>
      ["tournaments", "draws", tournamentId] as const,
    players: (tournamentId: string) =>
      ["tournaments", "players", tournamentId] as const,
    // A round_robin draw's standings (GET /tournaments/draws/{id}/standings).
    // Keyed by draw id so each selected draw's standings is card-isolated.
    standings: (drawId: string) =>
      ["tournaments", "draws", drawId, "standings"] as const,
    // A single_elimination draw's bracket (GET /tournaments/draws/{id}/bracket).
    // Keyed by draw id, parallel to standings.
    bracket: (drawId: string) =>
      ["tournaments", "draws", drawId, "bracket"] as const,
    // The caller's draw entries across EVERY tournament (GET
    // /users/me/registrations → DrawEntryResponse[]), SERVER-SCOPED to the auth
    // user — no uid is ever in the key. The NO-ARG cross-tournament variant of
    // registrationsMine (below), its OWN cache slot backing the "My entries" list.
    myRegistrations: () => ["tournaments", "my-registrations"] as const,
    myTeamEntries: () => ["tournaments", "my-team-entries"] as const,
    myPairingRequests: () => ["tournaments", "my-pairing-requests"] as const,
    myRegistrationPayments: () =>
      ["tournaments", "my-registration-payments"] as const,
    // The doubles partner picker's user search (S3). Calls the SHARED social
    // client (GET /social/users/search?q=) through a LOCAL tournaments hook — the
    // social module's useUserSearch can't be imported across the sibling-module
    // boundary. Keyed by the trimmed query so each distinct search gets its OWN
    // cache slot; the hook only RUNS at ≥ 2 chars (the server requires q
    // min_length=2). Read-only; no mutation invalidates it.
    partnerSearch: (query: string) =>
      ["tournaments", "partner-search", query] as const,
    // EPIC 1.2 multi-event registration. Per-event eligibility verdicts (GET
    // /tournaments/{id}/eligibility → EventEligibilityVerdict[]) and the caller's
    // entries SCOPED to this tournament (GET /tournaments/{id}/registrations/mine
    // → DrawEntryResponse[], live status, hydrated names). Both AUTH-gated +
    // SERVER-SCOPED to the token user; the SINGULAR "tournament" prefix keeps them
    // in their OWN cache slots, distinct from the cross-tournament
    // myRegistrations() above (which the GREEN migration leaves untouched). A
    // register/withdraw mutation invalidates these so the per-event row reflects
    // the server's refetched truth — never an optimistic local flip.
    eligibility: (tournamentId: string) =>
      ["tournament", tournamentId, "eligibility"] as const,
    registrationsMine: (tournamentId: string) =>
      ["tournament", tournamentId, "registrations", "mine"] as const,
    // EPIC 1.4 partner finder. The linkable partner-candidate pool for a draw
    // (GET /tournaments/draws/{id}/partner-candidates?q=). Keyed by draw id +
    // the trimmed query so each distinct search has its OWN cache slot (an empty
    // query is valid → the whole linkable pool). Called WITHOUT a query it is the
    // INVALIDATION PREFIX that matches every q-variant — so a successful propose
    // refetches the pool regardless of the active search.
    partnerCandidates: (drawId: string, query?: string): readonly string[] =>
      query !== undefined
        ? ["tournaments", "draws", drawId, "partner-candidates", query]
        : ["tournaments", "draws", drawId, "partner-candidates"],
    // The caller's pairing inbox for a draw (GET
    // /tournaments/draws/{id}/pairings/mine). A propose/respond mutation
    // invalidates this so the inbox reflects the server's refetched truth.
    pairingsMine: (drawId: string) =>
      ["tournaments", "draws", drawId, "pairings", "mine"] as const,
  },
} as const;
